import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Card,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Modal,
    Snackbar,
    Typography,
} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {$host} from '../../http/index';
import useSnackbar from '../../hooks/useSnackbar';
import {tags} from '../../utils/tags';
import MySnackbar from '../MySnackbar';
import EventMessageCard from "./EventMessageCard";
import TriggerForm from './TriggerForm';

const MailingAutoCreator = () => {
    const typeLabels = {
        REGULAR_PATIENT: "Постоянный клиент",
        FIRST_APPOINTMENT: "После первого приема",
        DATE: "Конкретная дата",
        INACTIVE_PATIENT: "Неактивный пациент",
        BIRTHDAY: "День рождения",
        REMIND_APPOINTMENT: "Напоминание о завтрашнем приеме",
        DRINKS_BEFORE_APPOINTMENT: "Предложение напитков перед приемом",
        PROMO_END: "Напоминание об окончании акций",
    };

    const [messages, setMessages] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const snack = useSnackbar();
    const [open, setOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [openModalEdit, setOpenModalEdit] = useState(false);
    const [copiedTag, setCopiedTag] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL || window.API_URL;

    useEffect(() => {
        fetchMessages();
        fetchBranches();
    }, []);

    useEffect(() => {
        fetchMessages();
    }, [openModal, openModalEdit]);

    const fetchMessages = async () => {
        try {
            const {data} = await $host.get('web/event_message');
            setMessages(data);
        } catch (error) {
            snack.handleChange(true, 'Ошибка загрузки сообщений', 'error');
        }
    };

    const fetchBranches = async () => {
        try {
            const {data} = await $host.get('web/clinic/branches');
            setAvailableBranches(data);
        } catch (error) {
            snack.handleChange(true, 'Ошибка загрузки филиалов', 'error');
        }
    };

    const handleSwitch = async (eventId, value) => {
        try {
            const response = await $host.patch(`web/event_message/${eventId}/switch`, {value}, {
                headers: {'Content-Type': 'application/json'},
            });
            return response.data;
        } catch (error) {
            alert(error.message || error);
        }
    }

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить рассылку?')) return;

        try {
            await $host.delete(`web/event_message/${id}`);
            setMessages((prev) => prev.filter((msg) => msg.id !== id));
            snack.handleChange(true, 'Рассылка удалена', 'success');
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Ошибка при удалении';
            snack.handleChange(true, errorMessage, 'error');
            await fetchMessages();
        }
    };

    const handleEdit = async (eventId) => {
        const {data} = await $host.get(`web/event_message/${eventId}`);
        setEditingId(data.id);
        setOpenModalEdit(true);
    };

    const handleCloseModal = () => setOpenModal(false);
    const handleCloseModalEdit = () => setOpenModalEdit(false);
    const handleClose = () => setOpen(false);
    const handleOpen = () => setOpen(true);

    const handleCopyTag = (tag) => {
        navigator.clipboard.writeText(tag);
        setCopiedTag(tag);
        setSnackbarOpen(true);
        setTimeout(() => setSnackbarOpen(false), 3000);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MySnackbar {...snack} />
            <Modal open={openModal} onClose={handleCloseModal}>
                <Card
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90vw',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography variant="h6" sx={{mb: 2}}>
                        Создание триггера
                    </Typography>
                    <TriggerForm
                        editingId={null}
                        onCloseModal={handleCloseModal}
                        availableBranches={availableBranches}
                    />
                </Card>
            </Modal>
            <Modal open={openModalEdit} onClose={handleCloseModalEdit}>
                <Card
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90vw',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography variant="h6" sx={{mb: 2}}>
                        Редактирование триггера
                    </Typography>
                    <TriggerForm
                        editingId={editingId}
                        onCloseModal={handleCloseModalEdit}
                        availableBranches={availableBranches}
                    />
                </Card>
            </Modal>
            <Button
                variant='contained'
                onClick={handleOpen}
                sx={{
                    position: 'fixed',
                    top: 140,
                    right: 20,
                    zIndex: 10,
                    fontSize: '12px',
                }}
            >
                Теги
            </Button>
            <Box sx={{p: 3, maxWidth: 800, margin: '0 auto'}}>
                <Button variant='contained' onClick={() => setOpenModal(true)}>
                    Создать триггер
                </Button>

                <Card sx={{p: 2}}>
                    <Typography variant='h6' gutterBottom>
                        Активные рассылки
                    </Typography>

                    <List>
                        {messages.map((msg) => (
                            <div key={msg.id}>
                                <ListItem
                                    secondaryAction={
                                        <>
                                            <IconButton onClick={() => handleEdit(msg.id)}>
                                                <EditIcon/>
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(msg.id)}>
                                                <DeleteIcon/>
                                            </IconButton>
                                        </>
                                    }
                                >
                                    <ListItemText
                                        primary={msg.templateText}
                                        secondary={
                                            // <>
                                            //     <div>Тип: {typeLabels[msg.type] || msg.type}</div>
                                            //     <div>Дата: {dayjs(msg.triggerDate).format('DD.MM.YYYY HH:mm')}</div>
                                            //     <div>
                                            //         Интервал: {msg.interval_value} {msg.interval_unit?.toLowerCase()}
                                            //     </div>
                                            //     <div>
                                            //         Филиал:{' '}
                                            //         {msg.sendToAllBranches
                                            //             ? 'Все филиалы'
                                            //             : availableBranches.find((b) => b.id === msg.branchId)?.name}
                                            //     </div>
                                            //     {msg.imageName && (
                                            //         <img
                                            //             src={`${API_URL}/static/${msg.imageName}`}
                                            //             alt='Прикрепленное'
                                            //             style={{
                                            //                 maxWidth: 100,
                                            //                 maxHeight: 100,
                                            //                 marginTop: 8,
                                            //             }}
                                            //         />
                                            //     )}
                                            // </>
                                            <EventMessageCard msg={msg} imageSrc={`${API_URL}/static/${msg.imageName}`}
                                                              type={typeLabels[msg.type] || msg.type}
                                                              mailToBranches={msg.sendToAllBranches
                                                                  ? 'Все филиалы'
                                                                  : availableBranches.find((b) => b.id === msg.branchId)?.name}
                                                              onSwitch={async (e) => {
                                                                  const result = await handleSwitch(msg.id, e.target.checked);
                                                                  if (result) {
                                                                      setMessages(prevMessages =>
                                                                          prevMessages.map(message =>
                                                                              message.id === msg.id
                                                                                  ? {
                                                                                      ...message,
                                                                                      isActive: !e.target.checked
                                                                                  }
                                                                                  : message
                                                                          )
                                                                      )

                                                                  }
                                                              }}
                                            />
                                        }
                                    />
                                </ListItem>
                                <Divider/>
                            </div>
                        ))}
                    </List>
                </Card>
            </Box>
            <Modal open={open} onClose={handleClose}>
                <Card
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80vw',
                        boxShadow: 24,
                        p: 4,
                        maxHeight: '70vh',
                        overflow: 'auto',
                    }}
                >
                    <Typography variant='h6'>Теги:</Typography>
                    {tags.map(
                        (tag) =>
                            tag.tag !== '{DoctorReviewLink}' &&
                            tag.tag !== '{PointsToAddOrWithDraw}' && (
                                <Box key={tag.name} style={{margin: '12px', fontSize: 12}}>
                                    <Divider style={{marginBottom: '12px'}} fullWidth/>
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        onClick={() => handleCopyTag(tag.tag)}
                                        style={{minWidth: 30, marginRight: '5px'}}
                                    >
                                        📋
                                    </Button>
                                    <Box
                                        style={{
                                            display: 'inline-block',
                                            backgroundColor: '#a9a9a9',
                                            borderRadius: '5px',
                                        }}
                                    >
                                        {tag.tag}
                                    </Box>{' '}
                                    - {tag.name}
                                </Box>
                            )
                    )}
                </Card>
            </Modal>
            <Snackbar
                open={snackbarOpen}
                message={`Тег "${copiedTag}" скопирован!`}
                anchorOrigin={{vertical: 'center', horizontal: 'center'}}
                autoHideDuration={3000}
            />
        </LocalizationProvider>
    );
};

export default MailingAutoCreator;
